import { warn } from '../../../core/js/utils/logger';

module.exports = /* @ngInject */
(
    $window,
    _,
    AnthroConfig
) => {
    return {
        /**
         * Get a query string that combines the passed-in query params
         * and the current params (it makes sure to override any duplicates).
         * @param       {Object}    queryParams     Query params
         * @return      {String}    String of passed-in and current query params
         */
        extendCurrentQueryString(queryParams) {
            const currentParams = this.getQueryParams();
            const newParams = _.merge(currentParams, queryParams);

            return this.getQueryString(newParams);
        },

        /**
         * Extend the current url with the passed-in query params
         * @param       {Object}    queryParams     Query params
         * @return      {String}                    Extended url
         */
        extendCurrentUrl(queryParams) {
            const qs = this.extendCurrentQueryString(queryParams);

            return '//' + this.getCurrentUrl() + qs;
        },

        /**
         * Get current URL with no query params
         * @param       {Object}    queryParams     Query params
         * @return      {String}                    Extended url
         */
        getCurrentUrl(queryParams) {
            return $window.location.host + $window.location.pathname;
        },

        /**
         * Get the passed-in query param value from the current url.
         * @param       {String}    param   Query Param
         * @return      {String}            Requested query param value, or empty string, if no value found
         */
        getQueryParamKey(param) {
            const query = window.location.search.substring(1);
            const vars = query.split('&');
            let key = '';
            let pair;
            let i;

            for (i = 0; i < vars.length; i++) {
                pair = vars[i].split('=');

                if (pair[0] === param) {
                    key = pair[1];
                }
            }

            return key;
        },

        /**
         * Get the query params from the current url as an object
         * @return      {Object}    Current query params
         */
        getQueryParams() {
            const queryParams =
                this.getSearchString()
                    .replace('?', '')
                    .split('&');

            return _.chain(queryParams)
                .map((param) => {
                    return param.split('=');
                })
                .zipObject()
                .value();
        },

        /**
         * Get the search string
         * @return      {String}    Current search string
         */
        getSearchString() {
            return $window.location.search;
        },

        /**
         * Convert an object of query params into a string
         * @param       {Object}    queryParams     Query Params
         * @return      {String}    String of query params
         */
        getQueryString(queryParams) {
            let qs = '';
            let key;

            for (key in queryParams) {
                if (queryParams[key]) {
                    // eslint-disable-next-line operator-assignment, no-multi-spaces
                    qs +=  (qs === '' ? '?' : '&') +
                                    key +
                                    '=' +
                                    queryParams[key].toString();
                }
            }

            return qs;
        },

        /**
         * Get a url relative to the current contextPath
         * @param   {String}    url         Url
         * @param   {Object}    queryParams Query params
         * @return  {String}                Url relative to the contextPath
         */
        getUrl(url, queryParams) {
            const qp = this.extendCurrentQueryString(queryParams);
            return `${AnthroConfig.location.contextPath}${url}${qp}`;
        },

        /**
         * Client-side redirects for hash-based routes (IE9).
         * Converts the pushstate url to hash-based
         * and refreshes page if necessary
         * @param  {String} url     Target url
         */
        ie9RouteFallback(url) {
            const partial = url.split('/').pop();
            const [slug, qp] = partial.split('?');
            const qs = qp ? `?${qp}` : '';

            if (slug && slug !== 'registry') {
                const path = `${AnthroConfig.location.contextPath}/registry#/${slug}${qs}`;

                if ($window.location.href !== `${$window.location.origin}${path}`) {
                    warn(`Redirecting to hash-based url: ${path}`);
                    $window.location = path;
                }
            }
        },

        /**
         * Test uri for a specific state
         * @param  {String}  state State to test uri for
         * @return {Boolean}       True, if in the current state; false, otherwise.
         * @example
         *     RouterService.isInState('/home')     // is the user in the home state?
         */
        isInState(state) {
            const regex = new RegExp(state);
            return regex.test($window.location.pathname);
        },

        /**
         * Update route in registry using the passed-in
         * partial route with given query params
         * @param       {String}    slug            Registry slug
         * @param       {Object}    queryParams     Query params
         * @return      {String}    Registry Url
         */
        registry(slug, queryParams) {
            const partial = `/registry${slug}`;
            const url = this.getUrl(partial, queryParams);

            return this.setLocation(url);
        },

        /**
         * Route to a passed-in url using the given query params
         * @param       {String}    url             Target Url
         * @param       {Object}    queryParams     Query params
         * @return      {String}    Routed url
         */
        route(url, queryParams) {
            const fullUrl = this.getUrl(url, queryParams);
            return this.setLocation(fullUrl);
        },

        /**
         * Set window.location to a passed-in url
         * @param       {String}    url     Target Url
         * @return      {String}    url     Target Url
         */
        setLocation(url) {
            $window.location.assign(url);
            return url;
        }
    };
};
